import React, { useCallback, } from 'react';
import { connect, } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { isEmpty, } from 'ramda';

import Icon from 'common/components/Icon';
import SummarySellerTiles from './SellerTiles';
import SummaryPanelDataBox from './PanelDataBox';
import { CART_TYPE } from '../../../../../../common/types';

type CommonContactDetails = {
    street: string,
    city: string,
};

type TaxPayerDetails = {
    name: string,
    nip: string,
};

type InternalFields = {
    subordinateInternalId: string,
}

type Props = Partial<{
    deliveryData: {
        firstName: string,
        lastName: string,
        name: string,
    } & CommonContactDetails,

    invoiceData: CommonContactDetails & TaxPayerDetails,
    payerData: CommonContactDetails & TaxPayerDetails & { email?: string, } & InternalFields,
    recipientData: CommonContactDetails & TaxPayerDetails & { schoolEmail?: string } & InternalFields | null,
    diffPayer: boolean,
    isOrderTypeSchool: boolean,
}>;

const SummaryBuyerPanel: React.FC<Props> = ({
    deliveryData,
    invoiceData,
    payerData,
    recipientData,
    diffPayer,
    isOrderTypeSchool,
}) => {
    const data = {
        delivery: {
            IconComponent: Icon.CardCar,
            header: 'Adres do wysyłki',
            content: [
                deliveryData?.name ?? '',
                `${deliveryData?.firstName} ${deliveryData?.lastName}`,
                deliveryData?.street ?? '',
                deliveryData?.city ?? '',
            ],
        },

        invoice: {
            IconComponent: Icon.CardPage,
            header: isOrderTypeSchool ? 'Dane na fakturze (Podmiot 2)' : 'Dane na fakturze',
            content: [
                invoiceData?.name ?? '',
                invoiceData?.street ?? '',
                invoiceData?.city ?? '',
                invoiceData?.nip ?? '',
            ],
        },

        payer: {
            IconComponent: Icon.CardMoney,
            header: isOrderTypeSchool ? 'Dane płatnika (Podmiot 3)' : 'Dane płatnika',
            subheader: 'Wybrano inne dane, niż dane na fakturze.',
            content: [
                payerData?.name ?? '',
                payerData?.street ?? '',
                payerData?.city ?? '',
                payerData?.nip ?? '',
                payerData?.subordinateInternalId ?? '',
                payerData?.email ?? '',
            ]
        },
        
        recipient: {
            IconComponent: Icon.CardPage,
            header: 'Dane odbiorcy (Podmiot 3)',
            content: [
                recipientData?.name ?? '',
                recipientData?.street ?? '',
                recipientData?.city ?? '',
                recipientData?.nip ?? '',
                recipientData?.subordinateInternalId ?? '',
                recipientData?.schoolEmail ?? '',
            ]
        }
    };

    const payerHeader = useCallback(
        () => (
            <Box display="flex"
                 mt={2}
                 flexDirection={{ xs: 'column', lg: 'column', }}
                 alignItems={{ xs: 'baseline', lg: 'start', }}>
                <Box flexBasis="44%">
                    <Typography variant="h5">
                        <Box display="flex">
                            <Box mr={0}
                                 component={data.payer.IconComponent}/>
                            {data.payer.header}
                        </Box>
                    </Typography>
                </Box>
                { diffPayer && !isOrderTypeSchool ?
                    (<Typography variant="caption">
                        ({data.payer.subheader})
                    </Typography>) : null }
            </Box>
        ),
        [ data.payer.IconComponent, data.payer.header, data.payer.subheader, diffPayer, ]
    );

    return (
        <SummarySellerTiles renderHeader="Kupujący">
            <Grid container
                  direction="row"
                  spacing={2}>
                <Grid item
                      md={6}
                      sm={12}
                      xs={12}>
                    <Box mt={2}>
                        <SummaryPanelDataBox content={data.delivery.content}
                                             IconComponent={data.delivery.IconComponent}
                                             header={data.delivery.header} />
                    </Box>
                    <Box mt={2}>
                        <SummaryPanelDataBox content={data.invoice.content}
                                             IconComponent={data.invoice.IconComponent}
                                             header={data.invoice.header} />
                    </Box>
                </Grid>
                <Grid item
                      md={6}
                      sm={12}
                      xs={12}>
                    {!isEmpty(payerData) && <SummaryPanelDataBox content={data.payer.content}
                                         IconComponent={data.payer.IconComponent}
                                         header={payerHeader} />}
                    {recipientData && <Box mt={2}>
                        <SummaryPanelDataBox content={data.recipient.content}
                                             IconComponent={data.recipient.IconComponent}
                                             header={data.recipient.header} />
                    </Box>}
                </Grid>
            </Grid>
        </SummarySellerTiles>
    );
};

type MappingType = {
    checkout: {
        values: any,
    }
};

const mapStateToProps = ({ checkout, }: MappingType) => {
    const shippingAddress = checkout.values?.shippingAddress;
    const billingAddress = checkout.values?.billingAddress;
    const diffPayer = Boolean(checkout.values?.differentData);
    const isOrderTypeSchool = checkout.values?.orderType === CART_TYPE.SCHOOL;
    const payerAddress = isOrderTypeSchool
        ? checkout.values?.payerData
        : checkout.values?.payerAddress;
    const recipientData = checkout.values?.recipientSchoolData;

    return {
        deliveryData: {
            name: shippingAddress?.company,
            firstName: shippingAddress?.firstName,
            lastName: shippingAddress?.lastName,
            street: `${shippingAddress?.street} ${shippingAddress?.streetNumber}`,
            city: `${shippingAddress?.postcode} ${shippingAddress?.city}`,
        },

        invoiceData: {
            name: billingAddress?.company,
            street: `${billingAddress?.street} ${billingAddress?.streetNumber}`,
            city: `${billingAddress?.postcode} ${billingAddress?.city}`,
            nip: billingAddress?.taxIdNumber,
        },

        payerData: {
            name: payerAddress?.company,
            street: `${payerAddress?.street} ${payerAddress?.streetNumber}`,
            city: `${payerAddress?.postcode} ${payerAddress?.city}`,
            nip: payerAddress?.taxIdNumber,
            subordinateInternalId: payerAddress?.subordinateInternalId,
            email: payerAddress?.schoolEmail,
        },
        
        recipientData: recipientData ? {
            name: recipientData?.company,
            street: `${recipientData?.street} ${recipientData?.streetNumber}`,
            city: `${recipientData?.postcode} ${recipientData?.city}`,
            nip: recipientData?.taxIdNumber,
            subordinateInternalId: recipientData?.subordinateInternalId,
            schoolEmail: recipientData?.schoolEmail,
        } : null,
        diffPayer,
        isOrderTypeSchool,
    };
};

export default connect(
    mapStateToProps,
)(SummaryBuyerPanel);
